import {
    AbstractTransformConf,
    AddField,
    ConvertTypeTransformConf,
    FieldTable,
    FilterConf,
    SqlLoadConf,
    StringTransformConf
} from '@tj/tj-dmp-lib-api';
import {EtlFileSourceInterface} from './etl-file-source.interface';
import {I18nConversionConfig, I18nConversionTransformConf} from './i18n-conversion.interface';

interface UtmMatchingTransformConf extends AbstractTransformConf {
    pattern?: 'MISTERFLY_PATTERN_1' | 'MISTERFLY_PATTERN_2';
    utmType?: 'SOURCE' | 'MEDIUM' | 'CAMPAIGN' | 'TERM' | 'CONTENT';
    regex?: string;
    keepRawOnNoMatch?: boolean;
    rules?: UtmRuleConfig[];
}

export interface UtmRuleConfig {
    utmPattern?: 'MISTERFLY_PATTERN_1' | 'MISTERFLY_PATTERN_2';
    utmType?: 'SOURCE' | 'MEDIUM' | 'CAMPAIGN' | 'TERM' | 'CONTENT';
    regex?: string;
    groupIndex?: number;
    regexMode?: 'PRESET' | 'CUSTOM' | 'DIRECT';
}

export interface ITargetFieldDestination {
    code: string;
    name: string;
    exampleValues?: string | string[];
    fieldDest: string;
    tableDest: string;
    meta: FieldTable;
    replace: boolean;
    replaceRegex: string[];
    replaceValue: string[];
    replaceAll: boolean[];
    pattern: string;
    origin: 'source' | 'static';
    value?: string;
    groupe?: number;
    filter?: FilterConf;
    reverseSearch?: boolean;
    codeTraductionAiriane?: ConvertTypeTransformConf.CodeTraductionAirianeEnum;
    i18nGroup?: string;
    srcConvert?: 'KEY' | 'VALUE';
    dataUpdated?: boolean;
    isConvertByDico?: boolean;
    i18nConversion?: I18nConversionConfig;
    localInput?: string;
    defaultValue?: string;
    utmMatching?: boolean;
    utmPattern?: 'MISTERFLY_PATTERN_1' | 'MISTERFLY_PATTERN_2';
    utmType?: 'SOURCE' | 'MEDIUM' | 'CAMPAIGN' | 'TERM' | 'CONTENT';
    utmRegex?: string;
    utmRegexMode?: 'PRESET' | 'CUSTOM';
    utmKeepRawOnNoMatch?: boolean;
    utmRules?: UtmRuleConfig[];
}

const searchString = '££££££';

export class TargetFieldDestination implements ITargetFieldDestination {
    code = null;
    exampleValues = null;
    fieldDest: string = null;
    meta: FieldTable;
    name: string;
    pattern: string;
    replace: boolean;
    replaceAll: boolean[];
    replaceRegex: string[];
    replaceValue: string[];
    tableDest: string = null;
    origin: 'source' | 'static';
    value = null;
    groupe: number;
    filter: FilterConf;
    reverseSearch = false;
    codeTraductionAiriane?: ConvertTypeTransformConf.CodeTraductionAirianeEnum = null;
    i18nGroup?: string;
    srcConvert?: 'KEY' | 'VALUE';
    dataUpdated = false;
    isConvertByDico = false;
    i18nConversion?: I18nConversionConfig;
    localInput?: string;
    defaultValue?: string = null;
    utmMatching = false;
    utmPattern: 'MISTERFLY_PATTERN_1' | 'MISTERFLY_PATTERN_2' = null;
    utmType?: 'SOURCE' | 'MEDIUM' | 'CAMPAIGN' | 'TERM' | 'CONTENT';
    utmRegex?: string;
    utmRegexMode: 'PRESET' | 'CUSTOM' = 'PRESET';
    utmKeepRawOnNoMatch = false;
    utmRules: UtmRuleConfig[] = [];

    static __init(t: ITargetFieldDestination): TargetFieldDestination {
        const r = new TargetFieldDestination();
        r.init(t);
        return r;
    }

    static __setAddField(a: AddField, meta: FieldTable) {
        const r = new TargetFieldDestination();
        r.origin = 'static';
        r.tableDest = a.tableDest;
        r.fieldDest = a.fieldDest;
        r.meta = meta;
        r.value = a.value;
        r.groupe = 0;
        r.filter = {
            value: null
        };
        return r;
    }

    static __setSqlConf(a: SqlLoadConf, meta: FieldTable, transforms: AbstractTransformConf[], exampleValues: Array<string>) {
        const r = new TargetFieldDestination();
        r.origin = 'source';
        if (a.discriminant.includes(searchString)) {
            r.groupe = parseInt(a.discriminant.split(searchString)[1]);
        } else {
            r.groupe = 0;
        }
        r.tableDest = a.tableDest;
        r.fieldDest = a.fieldDest;
        r.name = a.fieldSrc;
        r.code = a.fieldSrc;
        r.exampleValues = exampleValues;
        r.meta = meta;
        transforms.forEach(t => {
            if (t.typeTransform === 'StringTransformConf') {
                const s = t as StringTransformConf;
                if (s.regex && s.regex.length > 0) {
                    r.replace = true;
                    r.replaceAll = s.all;
                    r.replaceRegex = s.regex;
                    r.replaceValue = s.replacement;
                }
            } else if (t.typeTransform === 'ConvertTypeTransformConf') {
                const s = t as I18nConversionTransformConf;
                if (s.pattern) {
                    r.pattern = s.pattern;
                }
                r.reverseSearch = s.reverseSearch;
                r.codeTraductionAiriane = s.codeTraductionAiriane;
                r.i18nGroup = s.i18nGroup;
                r.i18nConversion = s.i18nConversion ? {...s.i18nConversion, onlyEnabled: false} : undefined;
                r.isConvertByDico = !!s.i18nConversion || s.isConvertByDico === true;
                r.srcConvert = s.srcConvert;
                r.localInput = s.localInput;
                r.defaultValue = s.defaultValue;
            } else if (t.typeTransform === 'UtmMatchingTransformConf') {
                const s = t as UtmMatchingTransformConf;
                r.utmMatching = true;
                r.utmPattern = s.pattern || null;
                r.utmType = s.utmType;
                r.utmRegex = s.regex;
                r.utmRegexMode = s.regex ? 'CUSTOM' : 'PRESET';
                r.utmKeepRawOnNoMatch = s.keepRawOnNoMatch === true;
                if (s.rules && s.rules.length > 0) {
                    r.utmRules = s.rules.map((rule) => ({
                        utmPattern: rule.utmPattern || s.pattern || null,
                        utmType: rule.utmType || s.utmType,
                        regex: rule.regex || null,
                        groupIndex: rule.groupIndex || 1,
                        regexMode: TargetFieldDestination.resolveUtmRuleRegexMode(rule.regex),
                    }));
                } else {
                    r.utmRules = [{
                        utmPattern: s.pattern || null,
                        utmType: s.utmType,
                        regex: s.regex || null,
                        groupIndex: 1,
                        regexMode: TargetFieldDestination.resolveUtmRuleRegexMode(s.regex),
                    }];
                }
            }
        });
        if (!r.utmMatching && TargetFieldDestination.isUtmDestinationField(r.fieldDest)) {
            // Default ON for provider_* UTM fields on first configuration
            r.utmMatching = true;
        }
        r.filter = {
            value: null
        };
        return r;
    }

    updateWithSourceField(source: EtlFileSourceInterface, groupe: number) {
        this.code = this.code ? this.code : source.code;
        this.name = this.name ? this.name : source.name;
        this.exampleValues = source.exampleValues;
        this.origin = 'source';
        this.groupe = groupe;
    }

    updateWithDestinationField(tableDest: string, fieldDest: string) {
        this.tableDest = tableDest;
        this.fieldDest = fieldDest;
        this.origin = 'source';
        this.groupe = 0;
        if (TargetFieldDestination.isUtmDestinationField(fieldDest)) {
            this.utmMatching = true;
            this.utmRegexMode = this.utmRegexMode || 'PRESET';
        }
    }

    newStaticField() {
        this.origin = 'static';
        this.groupe = 0;
    }

    init(t: ITargetFieldDestination) {
        this.code = t.code;
        this.exampleValues = t.exampleValues;
        this.fieldDest = t.fieldDest;
        this.meta = t.meta;
        this.name = t.name;
        this.pattern = t.pattern;
        this.replace = t.replace;
        this.replaceAll = t.replaceAll;
        this.replaceRegex = t.replaceRegex;
        this.replaceValue = t.replaceValue;
        this.tableDest = t.tableDest;
        this.origin = t.origin;
        this.value = t.value;
        this.groupe = t.groupe;
        this.reverseSearch = t.reverseSearch;
        this.codeTraductionAiriane = t.codeTraductionAiriane;
        this.i18nGroup = t.i18nGroup;
        this.isConvertByDico = t.isConvertByDico;
        this.i18nConversion = t.i18nConversion ? {...t.i18nConversion, onlyEnabled: false} : undefined;
        this.srcConvert = t.srcConvert;
        this.localInput = t.localInput;
        this.defaultValue = t.defaultValue;
        this.utmMatching = t.utmMatching ?? false;
        this.utmPattern = t.utmPattern ?? null;
        this.utmType = t.utmType;
        this.utmRegex = t.utmRegex;
        this.utmRegexMode = t.utmRegexMode ?? (t.utmRegex ? 'CUSTOM' : 'PRESET');
        this.utmKeepRawOnNoMatch = t.utmKeepRawOnNoMatch ?? false;
        this.utmRules = (Array.isArray(t.utmRules) && t.utmRules.length > 0)
            ? t.utmRules.map((rule) => ({...rule}))
            : [{
                utmPattern: this.utmPattern ?? null,
                utmType: this.utmType,
                regex: this.utmRegex ?? null,
                groupIndex: 1,
                regexMode: this.utmRegex ? 'CUSTOM' : 'PRESET',
            }];
    }

    getSqlConf(tenant: string): SqlLoadConf {
        return {
            discriminant: `${this.tableDest}${searchString}${this.groupe}`,
            tableDest: this.tableDest,
            fieldDest: this.fieldDest,
            schemaDest: tenant,
            fieldSrc: this.code,
            typeObject: 'SqlLoadConf'
        } as SqlLoadConf;
    }

    getAddField(tenant: string): AddField {
        return {
            tableDest: this.tableDest,
            fieldDest: this.fieldDest,
            schemaDest: tenant,
            type: this.meta.type,
            value: this.value
        } as AddField;
    }

    getTransfom(sqlConf: SqlLoadConf): AbstractTransformConf[] {
        const isUtmDestination = TargetFieldDestination.isUtmDestinationField(sqlConf.fieldDest);
        const shouldPersistLegacyReplace = this.replace && !(this.utmMatching && isUtmDestination);
        const transforms: AbstractTransformConf[] = [{
            typeTransform: 'ConvertTypeTransformConf',
            fieldSrc: this.code,
            fieldDest: sqlConf.fieldDest,
            tableDest: sqlConf.tableDest,
            type: this.meta.type,
            category: this.meta.categoryField,
            pattern: this.pattern,
            reverseSearch: this.reverseSearch,
            codeTraductionAiriane: this.codeTraductionAiriane,
            isConvertByDico: this.isConvertByDico && !this.utmMatching && !this.reverseSearch,
            i18nConversion: this.isConvertByDico && !this.utmMatching && !this.reverseSearch && this.i18nConversion
                ? {...this.i18nConversion, onlyEnabled: false} : undefined,
            localInput: this.localInput,
            defaultValue: this.defaultValue
        } as I18nConversionTransformConf];

        if (shouldPersistLegacyReplace) {
            const stringTransformConf: StringTransformConf = {
                typeTransform: 'StringTransformConf',
                fieldSrc: this.code,
                fieldDest: sqlConf.fieldDest,
                tableDest: sqlConf.tableDest,
            };
            transforms.push(stringTransformConf);
            stringTransformConf.regex = this.replaceRegex;
            stringTransformConf.replacement = this.replaceValue;
            stringTransformConf.all = this.replaceAll;
        }

        if (this.utmMatching) {
            const normalizedRules = (Array.isArray(this.utmRules) && this.utmRules.length > 0)
                ? this.utmRules
                : [{
                    utmPattern: this.utmPattern ?? null,
                    utmType: this.utmType,
                    regex: this.utmRegex || null,
                    groupIndex: 1,
                    regexMode: this.utmRegex ? 'CUSTOM' : 'PRESET',
                }];
            const regexMode = this.utmRegexMode || 'PRESET';
            const utmTransformConf: UtmMatchingTransformConf = {
                typeTransform: 'UtmMatchingTransformConf',
                fieldSrc: this.code,
                fieldDest: sqlConf.fieldDest,
                tableDest: sqlConf.tableDest,
                utmType: this.utmType,
                regex: regexMode === 'CUSTOM' ? this.utmRegex : null,
                keepRawOnNoMatch: this.utmKeepRawOnNoMatch === true,
                rules: normalizedRules.map((rule) => ({
                    utmPattern: rule.regexMode === 'DIRECT' ? null : (rule.utmPattern ?? this.utmPattern ?? null),
                    utmType: rule.regexMode === 'DIRECT' ? null : (rule.utmType || this.utmType),
                    // "DIRECT" mode maps the raw source value as-is: a catch-all regex with no
                    // capture group makes the engine return the full match unchanged.
                    regex: rule.regexMode === 'DIRECT' ? '.*' : (rule.regex || null),
                    groupIndex: rule.groupIndex || 1,
                    regexMode: rule.regexMode || (rule.regex ? 'CUSTOM' : 'PRESET'),
                }))
            };
            transforms.push(utmTransformConf);
        }

        return transforms;
    }

    private getDefaultUtmType(): 'SOURCE' | 'MEDIUM' | 'CAMPAIGN' | 'TERM' | 'CONTENT' {
        const field = (this.fieldDest || '').toLowerCase();
        if (field === 'provider_source') {
            return 'SOURCE';
        }
        if (field === 'provider_medium') {
            return 'MEDIUM';
        }
        if (field === 'provider_campaign') {
            return 'CAMPAIGN';
        }
        if (field === 'provider_term') {
            return 'TERM';
        }
        return 'CONTENT';
    }

    private static isUtmDestinationField(fieldDest: string): boolean {
        const f = (fieldDest || '').toLowerCase();
        return ['provider_source', 'provider_medium', 'provider_campaign', 'provider_term', 'provider_content'].includes(f);
    }

    private static resolveUtmRuleRegexMode(regex: string): 'PRESET' | 'CUSTOM' | 'DIRECT' {
        if (!regex) {
            return 'PRESET';
        }
        return regex === '.*' ? 'DIRECT' : 'CUSTOM';
    }

}
